import { faXmarkCircle } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GenericDialog, MultiSelectionDialog } from "@/components/dialogs";
import { InspectorRow, Probe } from "@/api/hitachi/rbi";
import { useInventory } from "../queries";
import { useModal } from "@/hooks";
import { useState } from "react";
import { v4 as uuid } from "uuid";

import styles from "./ProbeSelection.tsx.module.css";

export type ProbeSelectionProps = {
  row: InspectorRow;
  onAccept: (probes: Probe[]) => void;
};

export function ProbeSelection(props: Readonly<ProbeSelectionProps>) {
  const [probes, setProbes] = useState<Probe[]>(props.row.probes.length > 0 ? props.row.probes : [createEmptyProbe()]);

  const probeCalibrationUT = useInventory("UTCalibrationBlocks");
  const probeCalibrationET = useInventory("ETCalibrationBlocks");
  const probeSerialNumbersUT = useInventory("UTProbe");
  const probeSerialNumbersET = useInventory("ETProbe");
  const modal = useModal();

  function createEmptyProbe(): Probe {
    return {
      hitachiProbeId: uuid(),
      serialNumber: "",
      referenceDAC: "",
      transferCorrection: "",
      phaseAngle: "",
      sensitivity: "",
      calibrationBlocks: "",
    };
  }

  function handleFieldChanged(index: number, field: keyof Probe, value: string) {
    const newProbes = [...probes];
    newProbes[index] = { ...newProbes[index], [field]: value };
    setProbes(newProbes);
  }

  function handleAdd() {
    setProbes((prev) => [...prev, createEmptyProbe()]);
  }

  function handleRemove(probe: Probe) {
    const newProbes = probes.filter((x) => x !== probe);
    if (newProbes.length > 0) {
      setProbes(newProbes);
    } else {
      //empty probe for a row
      setProbes([createEmptyProbe()]);
    }
  }

  return (
    <GenericDialog
      caption="Probe Selection"
      buttons="OKCancel"
      onAccept={() => props.onAccept(probes)}
      className={styles.probeSelection}
    >
      <table className="table table-sm table-borderless">
        <thead>
          <tr>
            <th>Probe Serial Number</th>
            {(props.row.discipline === "UT" || props.row.discipline === "PMIUT") && (
              <>
                <th>Reference DAC (dB)</th>
                <th>Transfer Correlation (dB)</th>
              </>
            )}
            {(props.row.discipline === "ET" || props.row.discipline === "PMIET") && (
              <>
                <th>Phase Angle (deg)</th>
                <th>Sensitivity (dB)</th>
              </>
            )}
            <th>Calibration Blocks</th>
          </tr>
        </thead>
        <tbody>
          {probes.map((probe, index) => (
            <tr key={index}>
              {(props.row.discipline === "UT" || props.row.discipline === "PMIUT") && (
                <>
                  <td>
                    <select
                      className="form-select"
                      value={probe.serialNumber}
                      onChange={(e) => handleFieldChanged(index, "serialNumber", e.currentTarget.value)}
                      disabled={probeSerialNumbersUT.isLoading}
                    >
                      <option />
                      {probeSerialNumbersUT.data?.map((value) => (
                        <option key={value.itemId}>{value.serialNumber}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={probe.referenceDAC}
                      onChange={(e) => handleFieldChanged(index, "referenceDAC", e.currentTarget.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={probe.transferCorrection}
                      onChange={(e) => handleFieldChanged(index, "transferCorrection", e.currentTarget.value)}
                    />
                  </td>
                  <td>
                    <button
                      role="link"
                      className="btn btn-link"
                      onClick={() =>
                        modal.show(
                          <MultiSelectionDialog
                            caption="Calibration Blocks"
                            options={probeCalibrationUT.data?.map((x) => x.serialNumber)}
                            selected={probe.calibrationBlocks ? probe.calibrationBlocks.split(",") : []}
                            onAccept={(values) => handleFieldChanged(index, "calibrationBlocks", values.join(", "))}
                          />
                        )
                      }
                    >
                      {!probe.calibrationBlocks && "Click to select"}
                      {probe.calibrationBlocks &&
                        probe.calibrationBlocks.split(", ").map((value) => <div key={value}>{value}</div>)}
                    </button>
                  </td>
                </>
              )}
              {(props.row.discipline === "ET" || props.row.discipline === "PMIET") && (
                <>
                  <td>
                    <select
                      className="form-select"
                      value={probe.serialNumber}
                      onChange={(e) => handleFieldChanged(index, "serialNumber", e.currentTarget.value)}
                      disabled={probeSerialNumbersET.isLoading}
                    >
                      <option />
                      {probeSerialNumbersET.data?.map((value) => (
                        <option key={value.itemId}>{value.serialNumber}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={probe.phaseAngle}
                      onChange={(e) => handleFieldChanged(index, "phaseAngle", e.currentTarget.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={probe.sensitivity}
                      onChange={(e) => handleFieldChanged(index, "sensitivity", e.currentTarget.value)}
                    />
                  </td>
                  <td>
                    <button
                      role="link"
                      className="btn btn-link"
                      onClick={() =>
                        modal.show(
                          <MultiSelectionDialog
                            caption="Calibration Blocks"
                            options={probeCalibrationET.data?.map((x) => x.serialNumber)}
                            selected={probe.calibrationBlocks ? probe.calibrationBlocks.split(",") : []}
                            onAccept={(values) => handleFieldChanged(index, "calibrationBlocks", values.join(", "))}
                          />
                        )
                      }
                    >
                      {!probe.calibrationBlocks && "Click to select"}
                      {probe.calibrationBlocks &&
                        probe.calibrationBlocks.split(", ").map((value) => <div key={value}>{value}</div>)}
                    </button>
                  </td>
                </>
              )}
              <td className="align-middle">
                <FontAwesomeIcon
                  icon={faXmarkCircle}
                  role="button"
                  title="Delete"
                  onClick={() => handleRemove(probe)}
                />
              </td>
            </tr>
          ))}
          <tr>
            <td colSpan={4}>
              <button className="btn btn-link p-0" onClick={handleAdd}>
                Add Probe
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </GenericDialog>
  );
}
